import { BaseApi } from '@/api/base-api';
import type { BaseHttpRequest } from '@/api/generated-client/core/BaseHttpRequest';
import {
  CategoryCollectionDTO,
  CategoryCollectionsDTO,
  Collection,
  CollectionDTO,
  CreateCategoryCollectionRequest,
  PaginatedCollectionGamesResponse,
  PaginatedGameCollectionsResponse,
  SortDirectionDto as SortDirection,
  UpdateGameCollectionRequest,
} from '@/api/models';
import { HotColdCollection } from '@/api/response/collections/HotColdCollection';
import {
  ZingBrainAvailableCountries,
  ZingBrainFilters,
} from '@/api/response/zing-brain/zing-brain.types';
import {
  SourceCollection,
  SourceCollectionsResponse,
} from '@/api/response/collections/SourceCollection';
import { PaginatedCollectionPresetResponse } from '@/api/response/collections/PaginatedPresetCollection';
import { CreatePreset } from '@/api/response/collections/create-preset';
import {CollectionSortColumn, ResolveGamesResponse} from "@/open-api/models";

export class CollectionsApi extends BaseApi {
  constructor(readonly httpRequest: BaseHttpRequest) {
    super();
  }

  private controller = '/catalog/collections';

  async listCollections(
    query: {
      pageNumber?: number;
      pageSize?: number;
      pageSortDirection?: SortDirection;
      pageSortBy?: CollectionSortColumn;
      name?: string; // filter by collection name
      categoryName?: string; // filter by category name
      localeId?: number; // optional
      withDeleted?: boolean; // default false
      unlinkedOnly?: boolean; // default false
    },
    signal?: AbortSignal,
  ): Promise<PaginatedGameCollectionsResponse> {
    const response: PaginatedGameCollectionsResponse = await this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/collections?${this.queryToQueryString(query)}`,
      {},
      { signal },
    );
    return {
      ...response,
      items: response.items?.map((item) => this.toDomainCollection(item)) ?? [],
    };
  }

  async getCollection(
    collectionId: string,
    query?: { withDeleted?: boolean },
  ): Promise<CollectionDTO> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/collections/${collectionId}?${this.queryToQueryString(query || {})}`,
      {},
    );
  }

  async deleteCollection(collectionId: string): Promise<CollectionDTO> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[DELETE] /api/v1/collections/${collectionId}`,
      {},
    );
  }

  async resolveGames(slugs: Array<string>): Promise<ResolveGamesResponse> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[POST] /api/v1/dict/games/resolve`,
      {
        slugs
      },
    );
  }

  async listCollectionGames(
    collectionId: string,
    query?: {
      query?: string;
      search?: string;
      pageNumber?: number;
      pageSize?: number;
      pageSortDirection?: SortDirection;
    },
  ): Promise<PaginatedCollectionGamesResponse> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/collections/${collectionId}/games?${this.queryToQueryString(query || {})}`,
      {},
    );
  }

  async createCategoryCollection(
    categoryId: string,
    { collectionId, localeId }: CreateCategoryCollectionRequest,
  ): Promise<CategoryCollectionDTO> {
    const body = {
      collectionId,
      localeId: this.decodeDefaultLocaleId(localeId),
    };
    return this.httpRequest.rpcRequest(
      this.controller,
      `[POST] /api/v1/categories/${categoryId}/collections`,
      body,
    );
  }

  async getCategoryCollections(
    categoryId: string,
    query: { localeId: number },
  ): Promise<CategoryCollectionsDTO> {
    const localeId = this.decodeDefaultLocaleId(query.localeId);
    const qs = localeId ? this.queryToQueryString({ localeId }) : '';

    const response: CategoryCollectionsDTO = await this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/categories/${categoryId}/collections?${qs}`,
      {},
    );
    // if requested a default locale, return the collection with no locale id
    return {
      ...response,
      items: response.items.filter((item) =>
        localeId ? item.localeId === localeId : !item.localeId,
      ),
    };
  }

  async deleteCategoryCollection(
    categorySlug: string,
    collectionId: string,
    localeId: number,
  ): Promise<Record<string, never>> {
    const decodedLocaleId = this.decodeDefaultLocaleId(localeId);
    const qs = decodedLocaleId ? this.queryToQueryString({ localeId: decodedLocaleId }) : '';
    return this.httpRequest.rpcRequest(
      this.controller,
      `[DELETE] /api/v1/categories/${categorySlug}/collections/${collectionId}?${qs}`,
      {},
    );
  }

  async createCollection(body: {
    collectionSourceId: string;
    name: string;
    params?: Record<string, unknown>;
    games: Array<{
      gameSlug: string;
      position?: number;
      modificationType: 'INSERT' | 'UPDATE' | 'DELETE';
      isManual: boolean;
    }>;
  }): Promise<CollectionDTO> {
    return this.httpRequest.rpcRequest(this.controller, `[POST] /api/v1/collections`, body);
  }

  async getGamesRecommendations(params: Record<string, unknown>): Promise<
    | {
        values: Array<{ gameId: string; gameProviderId: string }>;
      }
    | { error: string; error_description: string }
  > {
    return this.httpRequest.request({
      method: 'POST',
      url: '/games-recommendations/zingbrain',
      body: params,
    });
  }

  async updateCollection(
    collectionId: string,
    body: UpdateGameCollectionRequest,
  ): Promise<CollectionDTO> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[PATCH] /api/v1/collections/${collectionId}`,
      body,
    );
  }

  async getHotColdCollections(query: { localeId: number }): Promise<HotColdCollection[]> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/collections/hot-cold?${this.queryToQueryString(query)}`,
      {},
    );
  }

  async getGameCollectionsFilters(): Promise<ZingBrainFilters> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/games-recommendations/filters',
    });
  }

  async getAvailableCountries(): Promise<ZingBrainAvailableCountries> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/games-recommendations/available-countries',
    });
  }

  async listCollectionSources(): Promise<SourceCollection[]> {
    const data = (await this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/collections/sources`,
      {},
    )) as SourceCollectionsResponse;

    return data.items;
  }

  async listCollectionPresets(
    collectionSourceId: string,
    query?: {
      query?: string;
    },
  ): Promise<PaginatedCollectionPresetResponse> {
    return await this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/collections/presets/search?collectionSourceId=${collectionSourceId}&${this.queryToQueryString(query || {})}`,
      {},
    );
  }

  async createCollectionPreset<T extends object>(dto: CreatePreset<T>): Promise<void> {
    return await this.httpRequest.rpcRequest(
      this.controller,
      `[POST] /api/v1/collections/presets`,
      dto,
    );
  }

  private decodeDefaultLocaleId(localeId: number): number | null {
    return localeId === 9999 ? null : localeId;
  }

  private encodeDefaultLocaleId(localeId: number | null): number {
    return localeId === null ? 9999 : localeId;
  }

  private toDomainCollection(collection: CollectionDTO): Collection {
    return {
      ...collection,
      localeId:
        collection.localeId === undefined
          ? undefined
          : this.encodeDefaultLocaleId(collection.localeId),
    };
  }
}
